import { createLogger } from 'redux-logger';
import { type Middleware, combineReducers, configureStore } from '@reduxjs/toolkit';
import { createReduxHistoryContext } from 'redux-first-history';
import { createBrowserHistory } from 'history';

import userReducer from '../slices/local/user';
import tourReducer from '../slices/local/tour';
import filterReducer from '../slices/local/filter';
import sortReducer from '../slices/local/sort';
import connectionReducer from '../slices/local/connection';
import playbackIssueReducer from '../slices/local/playback-issue';
import orchestratorReducer, { schedule } from '../slices/local/orchestrator';
import { apiAuth } from '../slices/api/auth';
import { apiCatalog } from '../slices/api/catalog';
import { apiCube } from '../slices/api/cube';
import { listenerMiddleware as orchestratorMiddleware } from '../middlewares/orchestrator';
import { hydrate, dehydrationMiddleware } from '../middlewares/ls-hydration';
import appConfig from '../../app.config';

const {
  createReduxHistory,
  routerMiddleware,
  routerReducer
} = createReduxHistoryContext({ history: createBrowserHistory() });

let loggerMiddleware: Middleware =
  () => next => action => next(action);

if(appConfig.env === 'development') {
  loggerMiddleware = createLogger({
    level: 'info',
    predicate: (_getState, action) => (
      action.type !== schedule.type
    ),
    collapsed: (_getState, { type }: { type: string }) => (
      type.endsWith('/middlewareRegistered') ||
      type.endsWith('executeQuery/pending') ||
          type.endsWith('executeQuery/fulfilled') ||
          type.endsWith('internalSubscriptions/subscriptionsUpdated') ||
          type === 'connection/setConnectionAvailability'
    ),
    titleFormatter: ({ type, meta }: { type: string, meta: any }, _time, took) => {
      if (type.endsWith('executeQuery/pending') ||
              type.endsWith('executeQuery/fulfilled') ||
              type.endsWith('executeMutation/pending') ||
              type.endsWith('executeMutation/fulfilled')) {
        return `api: ${type}/${meta?.arg?.endpointName} (${took.toFixed(2)} ms)`;
      }
      return `act: ${type} (${took.toFixed(2)} ms)`;
    }
  }) as Middleware;
}

const reducer = combineReducers({
  [apiAuth.reducerPath]: apiAuth.reducer,
  [apiCatalog.reducerPath]: apiCatalog.reducer,
  [apiCube.reducerPath]: apiCube.reducer,
  orchestrator: orchestratorReducer,
  connection: connectionReducer,
  playbackIssue: playbackIssueReducer,
  user: userReducer,
  tour: tourReducer,
  filter: filterReducer,
  sort: sortReducer,
  router: routerReducer
});

export const store = configureStore({
  preloadedState: hydrate(),
  reducer,
  middleware: getDefaultMiddleware => getDefaultMiddleware()
    .concat(orchestratorMiddleware.middleware)
    .concat(routerMiddleware)
    .concat(apiCube.middleware)
    .concat(apiCatalog.middleware)
    .concat(apiAuth.middleware)
    .concat(loggerMiddleware)
    .concat(dehydrationMiddleware),
  devTools: {
    predicate: (_getState, action) => (
      action.type !== schedule.type
    ),
    maxAge: 500
  }
});

export const history = createReduxHistory(store);

export type RootState = ReturnType<typeof reducer>;
export type AppDispatch = typeof store.dispatch;
